import { FC, useEffect, useMemo, useState } from "react";
import useIsComponentMounted from "../../../../../common/hooks/use-is-component-mounted";
import useRouteQueryParams from "../../../../../common/hooks/use-route-query-params";
import BillingsTaxiOrderListingByRailyRouteQueryParams from "./common/types/billings-taxi-order-listing-by-raily-route-query-params";
import BillingsTaxiOrderListingItem from "../common/types/billings-taxi-order-listing-item";
import BillingsTaxiOrderListingFilter, {
  BillingsTaxiOrderListingOrderStartDateFilter,
} from "../common/types/billings-taxi-order-listing-filter";
import BillingsTaxiOrderListingSortKey from "../common/types/billings-taxi-order-listing-sort-key";
import BillingsTaxiOrderListingSortSelectOption from "../common/types/billings-taxi-order-listing-sort-select-option";
import BillingsTaxiOrderListingSortHelper from "../common/billings-taxi-order-listing-sort.helper";
import usePagination from "../../../../../common/hooks/use-pagination";
import billingsTaxiOrderListingByRailyRouteQueryParamsService from "./common/billings-taxi-order-listing-by-raily-route-query-params.service";
import HeadingComponent from "../../../../../common/components/heading/heading.component";
import BillingsTaxiOrderListingStatusLegendComponent from "../common/status-legend/billings-taxi-order-listing-status-legend.component";
import BillingsTaxiOrderListingFiltersSelectComponent from "../common/filters/select/billings-taxi-order-listing-filters-select.component";
import ListingSortSelectComponent from "../../../../../common/components/listing/filter/sort/select/listing-sort-select.component";
import BillingsTaxiOrderListingFiltersBadgeListComponent from "../common/filters/list/billings-taxi-order-listing-filters-badge-list.component";
import CardComponent from "../../../../../common/components/card/card.component";
import BillingsTaxiOrderListingTableComponent from "../common/table/billings-taxi-order-listing-table.component";
import PaginationComponent from "../../../../../common/components/pagination/pagination.component";
import BillingsTaxiOrderListingViewBasicProps from "../common/types/billings-taxi-order-listing-view-basic-props";
import billingsTaxiOrderListingByRailyFactory from "./common/billings-taxi-order-listing-by-raily.factory";
import BillingsTaxiOrderListingFilterType from "../common/types/billings-taxi-order-listing-filter-type";
import DateRange from "../../../../../common/types/date-range";
import BillingsTaxiOrderListingByRailyTaxiCorporationSelectOption from "./common/types/billings-taxi-order-listing-by-raily-taxi-corporation-select-option";
import FormFieldComponent from "../../../../../common/components/form/field/form-field.component";
import billingsTaxiOrderListingRequestFactory from "../common/billings-taxi-order-listing-request.factory";
import { useAppContext } from "../../../../../context/app.context";
import fileDownloadService from "../../../../../common/utils/file-download/file-download.service";
import notificationService from "../../../../../common/utils/notification/notification.service";
import ButtonComponent from "../../../../../common/components/button/button.component";
import billingsTaxiOrderListingReportRequestFactory from "../common/billings-taxi-order-listing-report-request.factory";
import DateRangeInputComponent from "../../../../../common/components/form/input/date-range/date-range-input.component";
import SingleSelectComponent from "../../../../../common/components/form/select/single-select/single-select.component";
import BillingsTaxiOrderListingStatsSummaryComponent from "../common/stats-summary/billings-taxi-order-listing-stats-summary.component";
import BillingsTaxiOrderListingStatsSummary from "../common/types/billings-taxi-order-listing-stats-summary";
import billingsTranslationsHelper from "../../../../../languages/billings-translations.helper";
import useTaxiCorporationOrderBillingsList from "../../../../../common/services/taxi-corporation/order-billings/list/use-taxi-corporation-order-billings-list";
import useAbort from "../../../../../common/hooks/use-abort";
import TaxiCorporationOrderBillingsListParams from "../../../../../common/services/taxi-corporation/order-billings/list/taxi-corporation-order-billings-list-params";
import TaxiCorporationOrderBillingsList from "../../../../../common/services/taxi-corporation/order-billings/list/taxi-corporation-order-billings-list";
import BillingsTaxiOrderListingItemBillingStatus from "../common/types/billings-taxi-order-listing-item-billing-status";
import TaxiCorporationOrderBillingsDownload from "../../../../../common/services/taxi-corporation/order-billings/download/taxi-corporation-order-billings-download";
import TaxiCorporationOrderBillingsDownloadParams from "../../../../../common/services/taxi-corporation/order-billings/download/taxi-corporation-order-billings-download-params";
import taxiCorporationService from "../../../../../common/services/taxi-corporation/taxi-corporation.service";
import useTaxiCorporationList from "../../../../../common/services/taxi-corporation/list/use-taxi-corporation-list";
import TaxiCorporationList, {
  TaxiCorporationListItem,
} from "../../../../../common/services/taxi-corporation/list/taxi-corporation-list";

type BillingsTaxiOrderListingByRailyProps =
  BillingsTaxiOrderListingViewBasicProps;

const BillingsTaxiOrderListingByRailyComponent: FC<
  BillingsTaxiOrderListingByRailyProps
> = (props) => {
  const isComponentMounted = useIsComponentMounted();
  const { selectedAppLanguage } = useAppContext();

  const [routeQueryParams, setRouteQueryParams] =
    useRouteQueryParams<BillingsTaxiOrderListingByRailyRouteQueryParams>();

  const translations =
    billingsTranslationsHelper.getTaxiOrderBillingsListingTranslations();

  const [filters, setFilters] = useState<BillingsTaxiOrderListingFilter[]>(() =>
    billingsTaxiOrderListingByRailyRouteQueryParamsService.getFilters(
      routeQueryParams,
    ),
  );

  const taxiCorporationOrderBillingsList =
    useTaxiCorporationOrderBillingsList();
  const taxiCorporationOrderBillingsListAbort = useAbort();

  const taxiCorporationList = useTaxiCorporationList();
  const taxiCorporationListAbort = useAbort();

  const downloadAbort = useAbort();

  const [listingItems, setListingItems] = useState<
    BillingsTaxiOrderListingItem[]
  >([]);
  const [statsSummary, setStatsSummary] =
    useState<BillingsTaxiOrderListingStatsSummary | null>(null);
  const [totalResults, setTotalResults] = useState(0);

  const [isReportFetching, setIsReportFetching] = useState(false);

  const [selectedSortKey, setSelectedSortKey] =
    useState<BillingsTaxiOrderListingSortKey | null>(
      () =>
        billingsTaxiOrderListingByRailyRouteQueryParamsService.getSortKey(
          routeQueryParams,
        ) ?? null,
    );

  const [selectedTaxiCorporationUuid, setSelectedTaxiCorporationUuid] =
    useState<string | null>(
      () =>
        billingsTaxiOrderListingByRailyRouteQueryParamsService.getTaxiCorporationCompanyUuid(
          routeQueryParams,
        ) ?? null,
    );

  const onTaxiCorporationChange = (
    selectedOption: BillingsTaxiOrderListingByRailyTaxiCorporationSelectOption,
  ) => {
    setSelectedTaxiCorporationUuid(selectedOption.value.uuid);
    setPage(1);
  };

  const [taxiCorporationSelectOptions, setTaxiCorporationSelectOptions] =
    useState<BillingsTaxiOrderListingByRailyTaxiCorporationSelectOption[]>([]);

  const selectedTaxiCorporationSelectOption = taxiCorporationSelectOptions.find(
    (option) => option.value.uuid === selectedTaxiCorporationUuid,
  );

  const sortSelectOptions: BillingsTaxiOrderListingSortSelectOption[] = useMemo(
    () => BillingsTaxiOrderListingSortHelper.getSelectOptions(),
    [selectedAppLanguage],
  );

  const { page, pageSize, setPage, setPageSize } = usePagination({
    totalResults: totalResults,
    defaultPageSize:
      billingsTaxiOrderListingByRailyRouteQueryParamsService.getPageSize(
        routeQueryParams,
      ),
    defaultPage:
      billingsTaxiOrderListingByRailyRouteQueryParamsService.getPage(
        routeQueryParams,
      ),
  });

  const onTaxiCorporationFetchSuccess = (
    responseDataItems: TaxiCorporationListItem[],
  ) => {
    const taxiCorporationsSelectOptions =
      billingsTaxiOrderListingByRailyFactory.createTaxiCorporationSelectOptions(
        responseDataItems,
      );

    setTaxiCorporationSelectOptions(taxiCorporationsSelectOptions);
  };

  const handleTaxiCorporationsResponse = (response: TaxiCorporationList) => {
    onTaxiCorporationFetchSuccess(response.data);
  };

  useEffect(() => {
    taxiCorporationList.load({}, taxiCorporationListAbort.signal);
  }, []);

  useEffect(() => {
    if (!taxiCorporationList.data) {
      return;
    }
    handleTaxiCorporationsResponse(taxiCorporationList.data);
  }, [taxiCorporationList.data]);

  const onListingFetchSuccess = (
    response: TaxiCorporationOrderBillingsList,
  ) => {
    const listingItems =
      billingsTaxiOrderListingByRailyFactory.createListingItems(response.data);

    const statsSummary =
      billingsTaxiOrderListingByRailyFactory.createStatsSummary(response.stats);

    setListingItems(listingItems);
    setStatsSummary(statsSummary);
    setTotalResults(response.totalCount);
  };

  const fetchListing = () => {
    if (!selectedTaxiCorporationUuid) {
      return;
    }

    const params: TaxiCorporationOrderBillingsListParams =
      billingsTaxiOrderListingRequestFactory.createParams(
        page,
        pageSize,
        selectedTaxiCorporationUuid,
        filters,
        selectedSortKey,
      );

    taxiCorporationOrderBillingsList.load(
      params,
      taxiCorporationOrderBillingsListAbort.signal,
    );
  };

  useEffect(() => {
    if (!taxiCorporationOrderBillingsList.data) {
      return;
    }

    onListingFetchSuccess(taxiCorporationOrderBillingsList.data);
  }, [taxiCorporationOrderBillingsList.data]);

  useEffect(() => {
    if (!page || !pageSize || !selectedTaxiCorporationUuid) {
      return;
    }

    fetchListing();
    return taxiCorporationOrderBillingsListAbort.revoke;
  }, [filters, selectedTaxiCorporationUuid, selectedSortKey, page, pageSize]);

  useEffect(() => {
    if (!isComponentMounted) {
      return;
    }

    const queryParams =
      billingsTaxiOrderListingByRailyRouteQueryParamsService.createRouteQueryParams(
        selectedTaxiCorporationUuid,
        filters,
        selectedSortKey,
        page,
        pageSize,
      );

    setRouteQueryParams(queryParams);
  }, [selectedTaxiCorporationUuid, filters, selectedSortKey, page, pageSize]);

  const addNewFilter = (newFilter: BillingsTaxiOrderListingFilter) => {
    setFilters((curr) => [...curr, newFilter]);
    setPage(1);
  };

  const deleteFilter = (index: number) => {
    const newFilters = filters.filter((filter, _index) => _index !== index);

    setFilters(newFilters);
    setPage(1);
  };

  const deleteAllFilters = () => {
    setFilters([]);
    setPage(1);
  };

  const onPageChange = (page: number) => {
    setPage(page);
    window.scroll({ top: 0, behavior: "smooth" });
  };

  const onPageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
    setPage(1);
  };

  const selectedSortSelectOption = useMemo(() => {
    return (
      sortSelectOptions.find((item) => item.value === selectedSortKey) ?? null
    );
  }, [selectedSortKey, selectedAppLanguage]);

  const startDateFilterValue = filters.find(
    (filter) =>
      filter.type === BillingsTaxiOrderListingFilterType.ORDER_START_DATE,
  )?.value as BillingsTaxiOrderListingOrderStartDateFilter["value"] | undefined;

  const onOrderStartDateFilterValueChange = (dateRange: DateRange | null) => {
    if (!dateRange) {
      const newFilters = filters.filter(
        (filter) =>
          filter.type !== BillingsTaxiOrderListingFilterType.ORDER_START_DATE,
      );

      setFilters(newFilters);
      return;
    }

    const isFilterExists = !!filters.find(
      (filter) =>
        filter.type === BillingsTaxiOrderListingFilterType.ORDER_START_DATE,
    );

    const newFilter: BillingsTaxiOrderListingOrderStartDateFilter = {
      type: BillingsTaxiOrderListingFilterType.ORDER_START_DATE,
      value: {
        from: dateRange.from!,
        to: dateRange.to!,
      },
    };

    if (isFilterExists) {
      const newFilters = [
        ...filters.filter(
          (filter) =>
            filter.type !== BillingsTaxiOrderListingFilterType.ORDER_START_DATE,
        ),
        newFilter,
      ];

      setFilters(newFilters);
      setPage(1);
      return;
    }

    setFilters((curr) => [...curr, newFilter]);
    setPage(1);
  };

  const onReportFetchSuccess = (
    responseData: TaxiCorporationOrderBillingsDownload,
  ) => {
    fileDownloadService.downloadFromBlob(
      responseData.data,
      responseData.filename,
    );
  };

  const onReportFetchFailure = () => {
    notificationService.error(
      translations.report.failureDownloadingNotificationText,
    );
  };

  const fetchReport = () => {
    setIsReportFetching(true);

    const params: TaxiCorporationOrderBillingsDownloadParams =
      billingsTaxiOrderListingReportRequestFactory.createRequest(
        selectedTaxiCorporationUuid!,
        filters,
        selectedSortKey,
      );

    taxiCorporationService
      .getOrderBillingsReport(params, downloadAbort.signal)
      .then(onReportFetchSuccess)
      .catch(onReportFetchFailure)
      .finally(() => setIsReportFetching(false));
  };

  const onReportDownloadButtonClick = () => {
    fetchReport();
  };

  const ReportDownloadButton = (
    <ButtonComponent
      onClick={onReportDownloadButtonClick}
      type="primary"
      isLoading={isReportFetching}
      title={translations.report.downloadButtonTitle}
      data-test-id="billings-taxi-order-listing-by-raily-report-download-button"
    >
      {translations.report.downloadButtonText}
    </ButtonComponent>
  );

  const isReportDownloadButtonVisible = !!selectedTaxiCorporationUuid;
  const isListingContentVisible = !!selectedTaxiCorporationUuid;

  return (
    <div className="billings_taxi_order_listing">
      <HeadingComponent
        title={translations.header.headingText}
        actions={[
          ...props.actionButtons,
          isReportDownloadButtonVisible && ReportDownloadButton,
        ]}
      />
      <BillingsTaxiOrderListingStatusLegendComponent
        taxiCorporationUuid={selectedTaxiCorporationUuid}
        visibleStatuses={[
          BillingsTaxiOrderListingItemBillingStatus.CREATED,
          BillingsTaxiOrderListingItemBillingStatus.ACCEPTED,
          BillingsTaxiOrderListingItemBillingStatus.REJECTED,
          BillingsTaxiOrderListingItemBillingStatus.REOPENED,
          BillingsTaxiOrderListingItemBillingStatus.REOPEN_REQUEST_SENT,
        ]}
      />
      <FormFieldComponent label={translations.taxiCorporationLabel} isRequired>
        <SingleSelectComponent
          placeholder={translations.taxiCorporationPlaceholder}
          value={selectedTaxiCorporationSelectOption ?? null}
          options={taxiCorporationSelectOptions}
          onChange={(selectedOption) =>
            onTaxiCorporationChange(selectedOption!)
          }
          classNames={{
            root: "billings_taxi_order_listing_taxi_corporation_select",
          }}
          idForTesting={`billings-taxi-order-by-raily-listing-taxi-corporation-select`}
          isLoading={taxiCorporationList.isLoading}
          isDisabled={taxiCorporationList.isError}
          isSearchable
        />
      </FormFieldComponent>
      {isListingContentVisible && (
        <>
          <div className="billings_taxi_order_listing_tools">
            <div className="d-flex">
              <BillingsTaxiOrderListingFiltersSelectComponent
                filters={filters}
                onAddNewFilter={addNewFilter}
              />
              <DateRangeInputComponent
                date={startDateFilterValue ?? null}
                onChange={onOrderStartDateFilterValueChange}
                classNames={{ root: "ml-2" }}
                placeholder={
                  translations.filters.searchByStartDateSelectInputPlaceholder
                }
                idForTesting="billings-taxi-order-listing-by-raily-date-range"
              />
            </div>
            <ListingSortSelectComponent
              onChange={(option) => setSelectedSortKey(option?.value!)}
              options={sortSelectOptions}
              value={selectedSortSelectOption}
              idForTesting="billings-taxi-order-listing-by-raily-sort"
            />
          </div>
          <BillingsTaxiOrderListingFiltersBadgeListComponent
            filters={filters}
            onDeleteFilterClick={deleteFilter}
            onDeleteAllFiltersButtonClick={deleteAllFilters}
          />
          {!!listingItems.length && (
            <BillingsTaxiOrderListingStatsSummaryComponent
              isError={taxiCorporationOrderBillingsList.isError}
              isLoading={taxiCorporationOrderBillingsList.isLoading}
              statsSummary={statsSummary}
            />
          )}
          <CardComponent classNames={{ root: "mt-4" }}>
            <BillingsTaxiOrderListingTableComponent
              listingItems={listingItems}
              isError={taxiCorporationOrderBillingsList.isError}
              isLoading={taxiCorporationOrderBillingsList.isLoading}
            />
            <div className="billings_taxi_order_listing__pagination_wrapper">
              <PaginationComponent
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                page={page}
                pageSize={pageSize}
                totalResults={totalResults}
              />
            </div>
          </CardComponent>
        </>
      )}
    </div>
  );
};

export default BillingsTaxiOrderListingByRailyComponent;
